import { useEffect, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Icon } from '@/ui/Icon';
import { InputField } from '@/ui/Field';
import { Empty, Spinner } from '@/ui/bits';
import { MenuHead, MenuItem, MenuSep, Popover, usePopover } from '@/ui/Popover';
import { ApiError, errorMessage } from '@/lib/api';
import { plural } from '@/lib/format';
import { toast } from '@/lib/toast';
import type { Template } from '@shared/mail';
import { keys, saveTemplate, templatesQuery } from './data';
import { isBlankHtml } from './logic';

// The Template row: which template the email started from, a menu to change it, and saving the email as a new one.

export function TemplateRow({
  templateId,
  onApply,
  current,
  onSaved,
}: {
  templateId: string | null;
  onApply: (t: Template | null) => void;
  /** The email as it stands, for saving as a template. */
  current: () => { subject: string; html: string };
  onSaved: (t: Template) => void;
}) {
  const pop = usePopover('menu');
  const [saving, setSaving] = useState(false);
  const { data, isLoading, isError, refetch } = useQuery(templatesQuery);
  const chosen = templateId ? data?.find((t) => t.id === templateId) : null;

  useEffect(() => {
    if (!pop.open) setSaving(false);
  }, [pop.open]);

  return (
    <div className="cmp-row">
      <span className="rl" id="cmp-tpl-l">
        Template
      </span>
      <span className="rv">
        <span className={['tpl-chip', templateId ? '' : 'none'].filter(Boolean).join(' ')}>
          <Icon name="layers" />
          <span>{templateId ? (chosen?.name ?? 'Template') : 'No template'}</span>
        </span>
        <button type="button" className="btn btn-sm btn-ghost" aria-describedby="cmp-tpl-l" {...pop.anchorProps}>
          {templateId ? 'Change' : 'Choose'}
          <Icon name="chev-down" />
        </button>
      </span>
      <Popover state={pop} width={320} maxWidth={360} align="left" role={saving ? 'dialog' : 'menu'} label={saving ? 'Save as a template' : 'Templates'} className="tpl-menu">
        {saving ? (
          <SaveTemplate
            current={current}
            onBack={() => setSaving(false)}
            onSaved={(t) => {
              pop.close();
              onSaved(t);
            }}
          />
        ) : (
          <>
            <MenuHead>Templates</MenuHead>
            {isLoading ? (
              <div className="menu-note">
                <Spinner label="Loading templates" /> Loading templates
              </div>
            ) : isError ? (
              <div className="menu-note err">
                Templates are not loading.
                <button type="button" className="btn btn-sm btn-ghost" onClick={() => void refetch()}>
                  Try again
                </button>
              </div>
            ) : data && data.length === 0 ? (
              <Empty compact icon="layers" title="No templates yet">
                Save this email as the first one.
              </Empty>
            ) : (
              <div className="tpl-list">
                {data?.map((t) => (
                  <MenuItem key={t.id} icon="layers" checked={t.id === templateId} sub={t.uses ? `Used ${plural(t.uses, 'time')}` : 'Not used yet'} onSelect={() => onApply(t)}>
                    {t.name}
                  </MenuItem>
                ))}
              </div>
            )}
            <MenuSep />
            {templateId ? (
              <MenuItem icon="x" onSelect={() => onApply(null)}>
                No template
              </MenuItem>
            ) : null}
            <MenuItem icon="plus" keep onSelect={() => setSaving(true)}>
              Save this email as a template
            </MenuItem>
          </>
        )}
      </Popover>
    </div>
  );
}

function SaveTemplate({ current, onBack, onSaved }: { current: () => { subject: string; html: string }; onBack: () => void; onSaved: (t: Template) => void }) {
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: () => {
      const { subject, html } = current();
      return saveTemplate({ name: name.trim(), subject: subject.trim(), body: html });
    },
    onSuccess: (t) => {
      void qc.invalidateQueries({ queryKey: keys.templates });
      toast(
        <>
          Saved as the template <b>{t.name}</b>
        </>,
        { icon: 'layers' },
      );
      onSaved(t);
    },
    onError: (err) => setError(err instanceof ApiError ? (err.fields.name ?? err.fields.body ?? err.message) : errorMessage(err)),
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setError('Name the template');
      return;
    }
    if (isBlankHtml(current().html)) {
      setError('Write the message first');
      return;
    }
    save.mutate();
  };

  return (
    <form className="tpl-save" onSubmit={submit} noValidate>
      <div className="mhead">Save as a template</div>
      <InputField
        label="Template name"
        value={name}
        maxLength={80}
        onChange={(e) => {
          setName(e.target.value);
          setError(null);
        }}
        placeholder="Proposal follow-up"
        error={error}
        help="Keeps the subject and the message. {first} and {company} stay as merge fields."
        autoComplete="off"
      />
      <div className="sched-pick-f">
        <button type="button" className="btn btn-sm btn-ghost" onClick={onBack}>
          <Icon name="chev-left" />
          Back
        </button>
        <span className="spacer" />
        <button type="submit" className="btn btn-sm btn-primary" aria-busy={save.isPending || undefined} aria-disabled={save.isPending || undefined}>
          {save.isPending ? 'Saving' : 'Save template'}
        </button>
      </div>
    </form>
  );
}

/** The # button: puts {first} or {company} where the caret is. */
export function MergeMenu({ values, onInsert }: { values: { first: string | null; company: string | null }; onInsert: (field: '{first}' | '{company}') => void }) {
  const pop = usePopover('menu');
  return (
    <>
      <button type="button" className="icon-btn sm" aria-label="Insert merge field" data-tip="Insert merge field" onMouseDown={(e) => e.preventDefault()} {...pop.anchorProps}>
        <Icon name="hash" />
      </button>
      <Popover state={pop} width={280} align="left" label="Merge fields">
        <MenuHead>Merge fields</MenuHead>
        <MenuItem icon="user" sub={values.first ?? 'First name'} onSelect={() => onInsert('{first}')}>
          {'{first}'}
        </MenuItem>
        <MenuItem icon="building" sub={values.company ?? 'Company'} onSelect={() => onInsert('{company}')}>
          {'{company}'}
        </MenuItem>
        <div className="menu-note">Filled in for each person when the email goes out.</div>
      </Popover>
    </>
  );
}

/** A template body ready for the editor, with the merge fields filled for one known person. */
export function templateHtml(t: Template, fill: ((html: string) => string) | null): string {
  return fill ? fill(t.body) : t.body;
}
